import type { PaymentMethod } from '@noconflict/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../shared/api/supabase';
import {
  createCardSetup,
  getCardAdders,
  getOrganizationHasDefaultCard,
  listPaymentMethods,
  removePaymentMethod,
  setDefaultPaymentMethod,
  syncPaymentMethods,
} from './api';

export const paymentMethodKeys = {
  all: ['payment-method'] as const,
  org: (organizationId: string) => ['payment-method', organizationId] as const,
  hasDefault: (organizationId: string) =>
    ['payment-method', 'has-default', organizationId] as const,
  adders: (userIds: readonly string[]) =>
    ['payment-method', 'adders', [...userIds].sort()] as const,
};

async function sessionToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('forbidden');
  return token;
}

/** The cards as the mirror holds them. */
export function useOrgPaymentMethods(organizationId: string, enabled = true) {
  return useQuery({
    queryKey: paymentMethodKeys.org(organizationId),
    queryFn: () => listPaymentMethods(supabase, organizationId),
    enabled,
  });
}

/**
 * Whether a one-step submit is possible at all. Cheap and stable, so the project page may always ask; it tells
 * someone who may order or pay nothing about the card but that there is one.
 */
export function useOrgHasDefaultCard(organizationId: string, enabled = true) {
  return useQuery({
    queryKey: paymentMethodKeys.hasDefault(organizationId),
    queryFn: () => getOrganizationHasDefaultCard(supabase, organizationId),
    enabled,
    staleTime: 60_000,
  });
}

/** Who added each card. Missing names simply go unshown. */
export function usePaymentMethodAdders(userIds: readonly string[]) {
  return useQuery({
    queryKey: paymentMethodKeys.adders(userIds),
    queryFn: () => getCardAdders(supabase, userIds),
    enabled: userIds.length > 0,
    staleTime: 300_000,
  });
}

/** Every card change answers with the whole list, so the cache is replaced rather than invalidated. */
function useCardMutation<TInput>(
  organizationId: string,
  mutationFn: (token: string, input: TInput) => Promise<PaymentMethod[]>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: TInput) => mutationFn(await sessionToken(), input),
    onSuccess: (cards) => qc.setQueryData(paymentMethodKeys.org(organizationId), cards),
  });
}

/**
 * Brings the mirror up to date with Stripe. The Billing page runs this once when it opens, so a card removed in
 * the Stripe dashboard disappears here on the next visit.
 */
export const useSyncPaymentMethods = (organizationId: string) =>
  useCardMutation(organizationId, (token) => syncPaymentMethods(token, organizationId));

export const useSetDefaultPaymentMethod = (organizationId: string) =>
  useCardMutation(organizationId, (token, paymentMethodId: string) =>
    setDefaultPaymentMethod(token, organizationId, paymentMethodId),
  );

export const useRemovePaymentMethod = (organizationId: string) =>
  useCardMutation(organizationId, (token, paymentMethodId: string) =>
    removePaymentMethod(token, organizationId, paymentMethodId),
  );

/** One setup session per attempt: nothing is stored until the card exists. */
export function useCardSetup(organizationId: string) {
  return useMutation({
    mutationFn: async () => createCardSetup(await sessionToken(), organizationId),
  });
}
